import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: 0,
    transition: { delay: 0.3 + i * 0.18, duration: 1, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = ({ t }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const lines = [t.hero.line1, t.hero.line2, t.hero.line3];

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex items-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1559744609-36dd71bf3ac5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
          alt="Lake Geneva"
          className="w-full h-[120%] object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A101D]/80 via-[#0A101D]/70 to-[#0A101D]" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-36 pb-24 w-full">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="text-xs tracking-[0.35em] uppercase font-mono text-[#E2C376] mb-8 flex items-center gap-3"
        >
          <span className="w-10 h-px bg-[#D4AF37]" /> {t.hero.eyebrow}
        </motion.p>

        <h1 data-testid="hero-headline" className="font-serif text-4xl sm:text-5xl lg:text-6xl text-slate-50 leading-[1.08] tracking-tight mb-8">
          {lines.map((line, i) => (
            <span key={`${line}-${i}`} className="block overflow-hidden pb-1">
              <motion.span
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="visible"
                className={`block ${i === 2 ? "text-[#D4AF37] italic" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subtext"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="max-w-xl text-base text-slate-300 leading-relaxed mb-10"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-primary-cta"
            onClick={() => scrollTo("contact")}
            className="px-8 py-4 bg-[#D4AF37] text-[#0A101D] font-semibold tracking-wide hover:bg-[#E5C365] transition-all hover:-translate-y-0.5 rounded-sm"
          >
            {t.hero.cta1}
          </button>
          <button
            data-testid="hero-secondary-cta"
            onClick={() => scrollTo("services")}
            className="px-8 py-4 border border-[#D4AF37]/40 text-[#E2C376] tracking-wide hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-all rounded-sm"
          >
            {t.hero.cta2}
          </button>
        </motion.div>

        <motion.div
          data-testid="hero-badge-years"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-10 right-6 lg:right-10 hidden md:flex items-baseline gap-3 border-l border-[#D4AF37]/30 pl-5"
        >
          <span className="font-serif text-6xl text-[#D4AF37]">17+</span>
          <span className="text-xs uppercase tracking-[0.25em] text-slate-400 font-mono max-w-[120px]">{t.hero.badge}</span>
        </motion.div>
      </div>
    </section>
  );
};
